import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useStaticQuery, graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import sr from '@utils/sr';
import { srConfig } from '@config';
import { Icon } from '@components/icons';
import { usePrefersReducedMotion } from '@hooks';

const StyledProjectsGrid = styled.ul`
  ${({ theme }) => theme.mixins.resetList};

  a {
    position: relative;
    z-index: 1;
  }
`;

const StyledProject = styled.li`
  position: relative;
  display: grid;
  grid-gap: 10px;
  grid-template-columns: repeat(12, 1fr);
  align-items: center;

  @media (max-width: 768px) {
    ${({ theme }) => theme.mixins.boxShadow};
  }

  &:not(:last-of-type) {
    margin-bottom: 100px;

    @media (max-width: 768px) {
      margin-bottom: 70px;
    }

    @media (max-width: 480px) {
      margin-bottom: 30px;
    }
  }

  &:nth-of-type(odd) {
    .project-content {
      grid-column: 7 / -1;
      text-align: right;

      @media (max-width: 1080px) {
        grid-column: 5 / -1;
      }
      @media (max-width: 768px) {
        grid-column: 1 / -1;
        padding: 40px 40px 30px;
        text-align: left;
      }
      @media (max-width: 480px) {
        padding: 25px 25px 20px;
      }
    }
    .project-tech-list {
      justify-content: flex-end;

      @media (max-width: 768px) {
        justify-content: flex-start;
      }

      li {
        margin: 0 0 5px 20px;

        @media (max-width: 768px) {
          margin: 0 10px 5px 0;
        }
      }
    }
    .project-links {
      justify-content: flex-end;
      margin-left: 0;
      margin-right: -10px;

      @media (max-width: 768px) {
        justify-content: flex-start;
        margin-left: -10px;
        margin-right: 0;
      }
    }
    .project-image {
      grid-column: 1 / 8;

      @media (max-width: 768px) {
        grid-column: 1 / -1;
      }
    }
  }

  .project-content {
    position: relative;
    grid-column: 1 / 7;
    grid-row: 1 / -1;

    @media (max-width: 1080px) {
      grid-column: 1 / 9;
    }

    @media (max-width: 768px) {
      display: flex;
      flex-direction: column;
      justify-content: center;
      height: 100%;
      grid-column: 1 / -1;
      padding: 40px 40px 30px;
      z-index: 5;
    }

    @media (max-width: 480px) {
      padding: 30px 25px 20px;
    }
  }

  .project-overline {
    margin: 10px 0;
    color: var(--green);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    font-weight: 400;
  }

  .project-title {
    color: var(--lightest-slate);
    font-size: clamp(24px, 5vw, 28px);

    @media (min-width: 768px) {
      margin: 0 0 20px;
    }

    @media (max-width: 768px) {
      color: var(--white);

      a {
        position: static;

        &:before {
          content: '';
          display: block;
          position: absolute;
          z-index: 0;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
        }
      }
    }
  }

  .project-description {
    ${({ theme }) => theme.mixins.boxShadow};
    position: relative;
    z-index: 2;
    padding: 25px;
    border-radius: var(--border-radius);
    background-color: var(--light-navy);
    color: var(--light-slate);
    font-size: var(--fz-lg);

    @media (max-width: 768px) {
      padding: 20px 0;
      background-color: transparent;
      box-shadow: none;

      &:hover {
        box-shadow: none;
      }
    }

    a {
      ${({ theme }) => theme.mixins.inlineLink};
    }

    strong {
      color: var(--white);
      font-weight: normal;
    }
  }

  .project-tech-list {
    display: flex;
    flex-wrap: wrap;
    position: relative;
    z-index: 2;
    margin: 25px 0 10px;
    padding: 0;
    list-style: none;

    li {
      margin: 0 20px 5px 0;
      color: var(--light-slate);
      font-family: var(--font-mono);
      font-size: var(--fz-xs);
      white-space: nowrap;
    }

    @media (max-width: 768px) {
      margin: 10px 0;

      li {
        margin: 0 10px 5px 0;
        color: var(--lightest-slate);
      }
    }
  }

  .project-links {
    display: flex;
    align-items: center;
    position: relative;
    margin-top: 10px;
    margin-left: -10px;
    color: var(--lightest-slate);

    a {
      ${({ theme }) => theme.mixins.flexCenter};
      padding: 10px;

      &.external {
        svg {
          width: 22px;
          height: 22px;
          margin-top: -4px;
        }
      }

      svg {
        width: 20px;
        height: 20px;
      }
    }

    .cta {
      ${({ theme }) => theme.mixins.smallButton};
      margin: 10px;
    }
  }

  .project-image {
    ${({ theme }) => theme.mixins.boxShadow};
    grid-column: 6 / -1;
    grid-row: 1 / -1;
    position: relative;
    z-index: 1;

    &.phone {
      display: flex;
      align-items: center;
      justify-content: center;
      width: min(100%, 390px);
      max-width: 390px;
      justify-self: center;
      padding: 24px;
      background: radial-gradient(circle at 30% 20%, rgba(100, 255, 218, 0.18), transparent 45%),
        linear-gradient(140deg, rgba(17, 34, 64, 0.9), rgba(10, 25, 47, 0.9));

      a {
        overflow: hidden;
        background-color: var(--green);

        &:before {
          background-color: var(--navy);
        }
      }

      @media (max-width: 768px) {
        max-width: none;
      }
    }

    @media (max-width: 768px) {
      grid-column: 1 / -1;
      height: 100%;
      opacity: 0.25;
    }

    a {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      background-color: var(--green);
      border-radius: var(--border-radius);
      vertical-align: middle;

      &:hover,
      &:focus {
        background: transparent;
        outline: 0;

        &:before,
        .img {
          background: transparent;
          filter: none;
        }
      }

      &:before {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 3;
        transition: var(--transition);
        background-color: var(--navy);
        mix-blend-mode: screen;
      }
    }

    .img {
      border-radius: var(--border-radius);
      mix-blend-mode: multiply;
      filter: grayscale(100%) contrast(1) brightness(90%);

      .project-image.phone & {
        width: auto;
        height: auto;
        max-width: 220px;
        max-height: 360px;
        object-fit: contain;
        border-radius: 28px;
        background: #0b0f1a;
        box-shadow: 0 18px 50px rgba(0, 0, 0, 0.45), 0 0 0 6px rgba(10, 25, 47, 0.9);
        filter: none;
      }

      @media (max-width: 768px) {
        object-fit: cover;
        width: auto;
        height: 100%;
        filter: grayscale(100%) contrast(1) brightness(50%);
      }
    }

    .phone-gallery {
      position: relative;
      display: grid;
      place-items: center;
      width: min(100%, 240px);
      min-height: 420px;

      @media (max-width: 768px) {
        width: 100%;
        min-height: 100%;
      }
    }

    .phone-gallery-slide {
      grid-area: 1 / 1;
      opacity: 0;
      transform: translateY(10px) scale(0.97);
      transition: opacity 450ms ease, transform 450ms ease;

      &.is-active {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }

    .phone-gallery-dots {
      position: absolute;
      bottom: 16px;
      left: 50%;
      z-index: 4;
      display: flex;
      gap: 8px;
      transform: translateX(-50%);

      span {
        width: 7px;
        height: 7px;
        border-radius: 999px;
        background: rgba(255, 255, 255, 0.25);
        transition: var(--transition);

        &.is-active {
          width: 20px;
          background: var(--green);
        }
      }

      @media (max-width: 768px) {
        display: none;
      }
    }
  }
`;

const ProjectVisual = ({ title, href, imageStyle, images, prefersReducedMotion }) => {
  const hasMultipleImages = images.length > 1;
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    if (!hasMultipleImages || prefersReducedMotion) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setActiveImage(current => (current + 1) % images.length);
    }, 2600);

    return () => window.clearInterval(intervalId);
  }, [hasMultipleImages, images.length, prefersReducedMotion]);

  return (
    <div className={`project-image ${imageStyle === 'phone' ? 'phone' : ''}`}>
      <a href={href}>
        {imageStyle === 'phone' && hasMultipleImages ? (
          <div className="phone-gallery">
            {images.map((image, index) => (
              <GatsbyImage
                key={`${title}-${index}`}
                image={image}
                alt={`${title} screenshot ${index + 1}`}
                className={`img phone-gallery-slide ${index === activeImage ? 'is-active' : ''}`}
              />
            ))}

            <div className="phone-gallery-dots" aria-hidden="true">
              {images.map((_, index) => (
                <span
                  key={`${title}-dot-${index}`}
                  className={index === activeImage ? 'is-active' : ''}
                />
              ))}
            </div>
          </div>
        ) : (
          <GatsbyImage image={images[0]} alt={title} className="img" />
        )}
      </a>
    </div>
  );
};

ProjectVisual.propTypes = {
  title: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
  imageStyle: PropTypes.string,
  images: PropTypes.arrayOf(PropTypes.object).isRequired,
  prefersReducedMotion: PropTypes.bool,
};

const Featured = () => {
  const data = useStaticQuery(graphql`
    {
      featured: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/featured/" } }
        sort: { fields: [frontmatter___date], order: ASC }
      ) {
        edges {
          node {
            frontmatter {
              title
              cover {
                childImageSharp {
                  gatsbyImageData(width: 700, placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
                }
              }
              screenshots {
                childImageSharp {
                  gatsbyImageData(width: 700, placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
                }
              }
              tech
              github
              external
              imageStyle
            }
            html
          }
        }
      }
    }
  `);

  const featuredProjects = data.featured.edges.filter(({ node }) => node);
  const revealTitle = useRef(null);
  const revealProjects = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealTitle.current, srConfig());
    revealProjects.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
  }, []);

  return (
    <section id="projects">
      <h2 className="numbered-heading" ref={revealTitle}>
        Some Things I’ve Built
      </h2>

      <StyledProjectsGrid>
        {featuredProjects &&
          featuredProjects.map(({ node }, i) => {
            const { frontmatter, html } = node;
            const { external, title, tech, github, cover, cta, imageStyle, screenshots } =
              frontmatter;
            const screenshotImages =
              screenshots?.map(screenshot => getImage(screenshot)).filter(Boolean) || [];
            const images =
              screenshotImages.length > 0 ? screenshotImages : [getImage(cover)].filter(Boolean);
            const href = external ? external : github ? github : '#';

            return (
              <StyledProject key={i} ref={el => (revealProjects.current[i] = el)}>
                <div className="project-content">
                  <div>
                    <p className="project-overline">Featured Project</p>

                    <h3 className="project-title">
                      {external ? <a href={external}>{title}</a> : <span>{title}</span>}
                    </h3>

                    <div
                      className="project-description"
                      dangerouslySetInnerHTML={{ __html: html }}
                    />

                    {tech?.length > 0 && (
                      <ul className="project-tech-list">
                        {tech.map((tech, i) => (
                          <li key={i}>{tech}</li>
                        ))}
                      </ul>
                    )}

                    <div className="project-links">
                      {cta && (
                        <a href={cta} aria-label="Course Link" className="cta">
                          Learn More
                        </a>
                      )}
                      {github && (
                        <a href={github} aria-label="GitHub Link">
                          <Icon name="GitHub" />
                        </a>
                      )}
                      {external && !cta && (
                        <a href={external} aria-label="External Link" className="external">
                          <Icon name="External" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                <ProjectVisual
                  title={title}
                  href={href}
                  imageStyle={imageStyle}
                  images={images}
                  prefersReducedMotion={prefersReducedMotion}
                />
              </StyledProject>
            );
          })}
      </StyledProjectsGrid>
    </section>
  );
};

export default Featured;
